import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";

interface FileItem {
  file: File;
  preview: string;
}

interface UseFileUploadOptions {
  maxFiles?: number;
  acceptedFileTypes?: Record<string, string[]>;
  maxSizeBytes?: number;
}

interface UseFileUploadReturn {
  files: FileItem[];
  getRootProps: ReturnType<typeof useDropzone>["getRootProps"];
  getInputProps: ReturnType<typeof useDropzone>["getInputProps"];
  removeFile: (index: number) => void;
  clearFiles: () => void;
}

export function useFileUpload(
  options: UseFileUploadOptions,
): UseFileUploadReturn {
  const {
    maxFiles = 1,
    acceptedFileTypes = { "image/*": [".jpeg", ".jpg", ".png", ".gif"] },
    maxSizeBytes = 5 * 1024 * 1024, // 5MB default
  } = options;

  const [files, setFiles] = useState<FileItem[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, ...newFiles];
        return updatedFiles.slice(0, maxFiles);
      });
    },
    [maxFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles,
    maxSize: maxSizeBytes,
  });

  const removeFile = useCallback((index: number) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const clearFiles = useCallback(() => {
    setFiles((prevFiles) => {
      prevFiles.forEach((file) => URL.revokeObjectURL(file.preview));
      return [];
    });
  }, []);

  return {
    files,
    getRootProps,
    getInputProps,
    removeFile,
    clearFiles,
  };
}
